import {
  getTicketingBaseUrl,
  getGeotraxBaseUrl,
  getAuthBaseUrl,
  getMessagingBaseUrl,
  getSecurityBaseUrl,
} from './apiUtil';

// Small mapping to determine which service base a partial url should use
const SERVICE_TOKENS: { tokens: string[]; base: () => string }[] = [
  {
    tokens: ['tickets', 'statuses', 'orders', 'qualityfactors', 'tertiarydata'],
    base: getTicketingBaseUrl,
  },
  {
    tokens: ['regions', 'trucks', 'drivers', 'devices', 'plants', 'checklists', 'tenants'],
    base: getGeotraxBaseUrl,
  },
  { tokens: ['auth', 'driver'], base: getAuthBaseUrl },
  { tokens: ['messaging', 'group', 'messages', 'conversations'], base: getMessagingBaseUrl },
  { tokens: ['flags', 'featureflag-assignment'], base: getSecurityBaseUrl },
];

export function isAbsoluteUrl(url?: string | null): boolean {
  if (!url) return false;
  return /^https?:\/\//i.test(url);
}

function determineBaseForPath(path: string): (() => string) | null {
  const lower = path.toLowerCase();
  const found = SERVICE_TOKENS.find(entry =>
    entry.tokens.some(t => lower.includes(`/${t}`) || lower.startsWith(`${t}/`)),
  );
  return found ? found.base : null;
}

/**
 * Expand a resource url (which may be full or partial) into a full absolute url.
 * - If url is already absolute (http(s)://) it is returned as-is.
 * - If url is a partial path (starts with '/') it will be prefixed with a detected base.
 * - If url is an id-only or short string (like "tickets/1" or "ticket/1"), it will be normalized and prefixed.
 * - If the service base cannot be detected and defaultBase is provided, defaultBase() will be used.
 * - If nothing can be done, the original url is returned.
 */
export function expandResourceUrl(url?: string | null, defaultBase?: () => string): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (trimmed.length === 0) return null;

  if (isAbsoluteUrl(trimmed)) return trimmed;

  // ensure it starts with '/'
  let path = trimmed;
  if (!path.startsWith('/')) path = `/${path}`;

  // try to detect correct base
  const baseFn = determineBaseForPath(path) ?? defaultBase;
  if (baseFn) {
    // baseFn may return a string without trailing slash per apiUtil implementations
    return `${baseFn()}${path}`;
  }

  // fallback - if no base detected, return original trimmed value
  return trimmed;
}

export default {
  expandResourceUrl,
  isAbsoluteUrl,
};
